import { Outlet, useParams } from 'react-router-dom';
import { ClientHeader } from '../components/clients/ClientHeader.js';
import { ApiError } from '../lib/api-client.js';
import { useClientQuery } from '../queries/use-client.js';

/**
 * Layout for `/clients/:id` and its sub-tabs.
 *
 * Renders the {@link ClientHeader} (logo + name + sub-tab pills) above
 * an outlet for the active sub-tab page. When the parent client query
 * 404s we surface a card here so every sub-tab inherits the same
 * not-found behavior without each having to repeat the check.
 */
export function ClientDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { error, isError } = useClientQuery(id);

  if (isError && error instanceof ApiError && error.status === 404) {
    return (
      <div className="flex grow flex-col">
        <div className="flex grow flex-col items-center justify-center p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
            Client not found
          </h2>
          <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
            No tenant with id <span className="font-mono">{id ?? '(none)'}</span> exists, or you
            don't have permission to view it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex grow flex-col overflow-hidden">
      <ClientHeader />
      <div className="grow overflow-y-auto p-4">
        <Outlet />
      </div>
    </div>
  );
}
